import { type FlowEndInfo } from "../../types";
import { Divider } from "semantic-ui-react";
import { BackButton } from "../BackButton/BackButton";
import LabelWithInfoTooltip from "components/labelWithInfoTooltip/labelWithInfoTooltip";
import BasicLinkButton from "components/buttons/linkButtons/baseLinkButton/BasicLinkButton";
import FlowEndForm from "./flowEndForm/FlowEndForm";
import NextFlow from "./nextFlow/NextFlow";
import NextLink from "./nextLink/NextLink";
import styles from "./flowEndPanel.module.scss";
import { useMemo } from "react";

export interface FlowEndPanelProps {
  onBackButtonClick: () => void;
  flowEndInfo: FlowEndInfo;
  onFlowEndChange: (flowEnd: FlowEndInfo) => void;
  isReadOnly: boolean;
}

const FlowEndPanel = (props: FlowEndPanelProps) => {
  const handleRemove = () => {
    props.onFlowEndChange({
      completedMessage: props.flowEndInfo.completedMessage,
      nextFlow: undefined,
      nextLinkUrl: undefined,
      nextLinkLabel: undefined,
    });
  };

  const nextFlow = useMemo(
    () => (
      <NextFlow isReadOnly={props.isReadOnly} flowEndInfo={props.flowEndInfo} onFlowEndChange={props.onFlowEndChange} />
    ),
    [props.flowEndInfo, props.isReadOnly, props.onFlowEndChange],
  );

  const nextLink = useMemo(
    () => (
      <NextLink isReadOnly={props.isReadOnly} flowEndInfo={props.flowEndInfo} onFlowEndChange={props.onFlowEndChange} />
    ),
    [props.flowEndInfo, props.isReadOnly, props.onFlowEndChange],
  );

  const connectionSection = useMemo(() => {
    if (!props.flowEndInfo.nextLinkUrl && !props.flowEndInfo.nextFlow) {
      return (
        <>
          {nextFlow}
          {nextLink}
        </>
      );
    }
    if (props.flowEndInfo.nextFlow) return nextFlow;
    return nextLink;
  }, [nextFlow, nextLink, props.flowEndInfo.nextFlow, props.flowEndInfo.nextLinkUrl]);

  return (
    <div className={styles.root}>
      <BackButton title="End of Flow" onClick={props.onBackButtonClick} />
      <Divider />
      <div className={styles["info-content"]}>
        <span>This will trigger the end of flow state for end users.</span>
      </div>
      <FlowEndForm
        isReadOnly={props.isReadOnly}
        flowEndInfo={props.flowEndInfo}
        onFlowEndChange={props.onFlowEndChange}
      />
      <div className={styles.connectionSection}>
        <div className={styles.header}>
          <div className={styles.headerContent}>
            <LabelWithInfoTooltip
              label="Connect to"
              info="Connect another flow or external link to the end of this flow."
            />
            {(props.flowEndInfo.nextFlow || props.flowEndInfo.nextLinkUrl) && !props.isReadOnly && (
              <BasicLinkButton text="Remove" onClick={handleRemove} />
            )}
          </div>
        </div>
        <div className={styles.connectionOptions}>{connectionSection}</div>
      </div>
    </div>
  );
};

export default FlowEndPanel;
